<template>
  <div
    v-if="articles.length"
    :id="widget.id"
    class="space-y-6"
    :class="{ 'content-visibility-10': widget.type === 'cards_10', 'content-visibility-6': widget.type === 'cards_6' }"
  >
    <div v-if="widget.banner" class="mt-2 sm:mt-0">
      <a :href="widget.banner.href">
        <OImage v-if="isMobile" loading="lazy" :src="widget.banner.mobile" />
        <OImage v-else loading="lazy" :src="widget.banner.desktop" />
      </a>
    </div>
    <div v-else class="flex items-center font-medium px-4 sm:px-0 mt-2 sm:mt-0">
      <OIcon :icon="`category/${widget.icon}`" class="sm:mr-2.5 mr-2 -ml-0.5" svg-class="w-8 h-8 sm:w-10 sm:h-10" />
      <h2 class="leading-18 text-20 font-semibold sm:text-24 mr-2">{{ widget.title }}</h2>
      <div class="border-t border-[#E2E5ED] flex-grow"></div>
    </div>
    <div class="flex flex-wrap justify-between items-stretch">
      <template v-for="(article, index) in articles">
        <ArticleWidgetCard
          :key="`${widget.type}_${article._id}_${isMobile ? 'mobile' : 'desktop'}`"
          :article="article"
          :device-style="{ desktop: widget.type === 'cards_10' ? 'width: 538px' : 'width: 351px' }"
          class="mb-6"
          :data-layer="{ name: `Anasayfa - ${widget.title}`, position: index + 1 }"
          :variant="
            isMobile ? (widget.type === 'cards_10' ? (index % 8 < 3 ? 'big' : 'small') : index % 5 < 2 ? 'big' : 'small') : widget.type
          "
        />
        <AdController
          :key="`${widget.type}_${article._id}_${isMobile ? 'mobile' : 'desktop'}_ad`"
          :unit="widget.id === 'trends' ? 'trendler' : widget.data.length === 8 ? 'widget10' : 'widget6'"
          :parent-index="index"
          :targeting="{ widget: [widget.id] }"
          class="mb-6 sm:rounded-md bg-white u-center w-full sm:w-auto"
        />
      </template>
      <div v-if="showMore" class="flex items-center flex-grow justify-center cursor-pointer w-full" @click="loadMoreArticle">
        <div class="text-[#9B9B9B] text-md font-semibold mx-3 flex items-center mb-2 sm:mb-0">
          <div class="w-[34px] h-[34px] bg-[#9B9B9B] rounded-full mr-4 u-center" :class="{ 'animate-spin': loading }">
            <OIcon icon="more" svg-class="text-white" />
          </div>
          <div class="uppercase">{{ $t('main.show-more') }}</div>
        </div>
      </div>
      <OLink v-if="!showMore && isTrends" class="trends__all flex items-center flex-grow justify-center cursor-pointer" href="/trendler">
        <div class="text-[#9B9B9B] text-md font-semibold mx-3 flex items-center mb-2 sm:mb-0">
          <div class="w-[34px] h-[34px] bg-[#9B9B9B] rounded-full mr-4 u-center">
            <OIcon icon="category/guncel-white" svg-class="text-white" />
          </div>
          TÜM İÇERİKLERİ GÖR
        </div>
      </OLink>
    </div>
  </div>
</template>
<script>
import { defineComponent, ref, useContext, computed, useFetch, useRoute } from '@nuxtjs/composition-api';
import ArticleWidgetCard from '@/modules/card/templates/homepage/ArticleWidgetCard';
import AdController from '@/modules/ads/components/AdController';
import { useDevice } from '~/composables/useDevice';
import { OLink, OImage, OIcon } from '~/modules/common/components/ui';

export default defineComponent({
  components: { ArticleWidgetCard, AdController, OLink, OImage, OIcon },
  props: {
    data: {
      type: Object,
    },
    widgetIndex: {
      type: Number,
    },
  },
  setup(props) {
    const route = useRoute();
    const { isMobile } = useDevice();
    const { $service } = useContext();
    const page = ref(2);
    const loading = ref(false);
    const widget = ref(props.data);
    const articles = ref(widget.value.data);
    const isTrends = widget.value.href === '/trendler';

    useFetch(async () => {
      if (route?.value?.query?.widgetId !== undefined && route?.value?.query?.widgetId === widget.value.id) {
        try {
          for (let i = 2; i <= parseInt(route?.value?.query?.pageNum); i++) {
            const { data } = await $service.homepage.getHomePageWidgetData({
              widgetId: route?.value?.query?.widgetId,
              page: i,
            });
            articles.value = articles.value.concat(data);
            page.value++;
          }
        } catch (error) {
        } finally {
          loading.value = false;
        }
      }
    });

    const loadMoreArticle = async () => {
      loading.value = true;
      try {
        const { data } = await $service.homepage.getHomePageWidgetData({
          widgetId: widget.value.id,
          page: page.value,
        });
        articles.value = articles.value.concat(data);
        const newurl = `/?widgetId=${widget.value.id}&pageNum=${page.value}`;
        history.replaceState({ url: newurl }, '', newurl);
        page.value++;
      } catch (error) {
      } finally {
        loading.value = false;
      }
    };

    const showMore = computed(() => {
      const limit = widget.value.type === 'cards_10' ? 8 : 5;
      const hasNextPage = articles.value.length % limit === 0;
      return widget.value.showMore && page.value <= 3 && hasNextPage;
    });

    return {
      isMobile,
      widget,
      articles,
      loadMoreArticle,
      page,
      loading,
      showMore,
      isTrends,
    };
  },
});
</script>

<style lang="scss">
.content-visibility-6 {
  content-visibility: auto;
  contain-intrinsic-size: 1325px;
  @include sm() {
    content-visibility: visible;
    contain-intrinsic-size: 780px;
  }
}
.content-visibility-10 {
  content-visibility: auto;
  contain-intrinsic-size: 2235px;
  @include sm() {
    content-visibility: visible;
    contain-intrinsic-size: 986px;
  }
}
</style>
